import {
  CreateExperienceProductDto,
  CreateExperienceProductDtoSchema,
  ExperienceProductDto,
  ExperienceProductVariantDto,
  ExperienceVariationPricesDto,
  S3FileLocationDto,
} from "@intelligems/ig-types";
import * as rtk from "@reduxjs/toolkit";
import { type PayloadAction, createSelector } from "@reduxjs/toolkit";

import { testProductsFromGqlId } from "../../api/formatters/products";
import { toGqlId } from "../../api/formatters/shopify";
import { VariationFe } from "../../types/schemas/variations";
import {
  IExperienceProductsState,
  sliceInitialExperienceProductsState,
} from "../types/experienceProducts";

const experienceProducts = rtk.createSlice({
  name: "experienceProducts",
  initialState: sliceInitialExperienceProductsState,
  reducers: {
    resetExperienceProducts: (draft) => {
      draft = sliceInitialExperienceProductsState;
      return draft;
    },
    modResetExperienceProducts: (draft) => {
      draft = { ...draft, experienceProducts: [] };
      return draft;
    },
    initExperienceProducts: (
      draft,
      action: PayloadAction<ExperienceProductDto[]>
    ) => {
      draft.isSuccess;
      draft.experienceProducts = action.payload;
    },
    initProductState: (
      draft,
      action: PayloadAction<IExperienceProductsState>
    ) => {
      draft = action.payload;
      return draft;
    },
    initProductsFeFromDto: (
      draft,
      action: PayloadAction<{
        products: ExperienceProductDto[];
        reformatProductIds?: boolean;
      }>
    ) => {
      if (!action.payload.products) {
        return draft;
      } else {
        draft.isSuccess = true;
        let tempProducts = action.payload.products;
        if (action.payload.reformatProductIds) {
          tempProducts = testProductsFromGqlId(action.payload.products);
        }
        draft.experienceProducts = tempProducts;
        draft.storedExperienceProducts = tempProducts;
      }

      return draft;
    },
    setAllExperienceProductsLoaded: (draft, action: PayloadAction<boolean>) => {
      if (!draft.isSuccess) return draft;
      draft.allProductsLoaded = action.payload;
      return draft;
    },
    removeExperienceProducts: (
      draft,
      action: PayloadAction<{ productIds: string[] }>
    ) => {
      if (!draft.isSuccess) return draft;
      draft.experienceProducts = draft.experienceProducts.filter(
        (testProd) =>
          !action.payload.productIds.some(
            (id) => toGqlId(id, "Product") === toGqlId(testProd.id, "Product")
          )
      );
      return draft;
    },
    setExperienceProducts: (
      draft,
      action: PayloadAction<{
        products: CreateExperienceProductDto[];
        isInitialLoad: boolean;
      }>
    ) => {
      // if (!draft.isSuccess) return draft;
      draft.experienceProducts = action.payload.products;
      if (action.payload.isInitialLoad) {
        draft.storedExperienceProducts = action.payload.products;
      }
      return draft;
    },
    setIsUploadingExpProducts: (draft, action: PayloadAction<boolean>) => {
      draft.isUploadingProducts = action.payload;
      return draft;
    },
    setExperienceProductsLocation: (
      draft,
      action: PayloadAction<{
        bucket: S3FileLocationDto;
      }>
    ) => {
      draft.productsLocation = action.payload.bucket;
      draft.isFileLoaded = true;
      return draft;
    },
    deleteVariationProducts: (
      draft,
      action: PayloadAction<{
        index: number;
      }>
    ) => {
      draft.experienceProducts = draft.experienceProducts.map(
        (product: CreateExperienceProductDto) => ({
          ...product,
          variants: product.variants.map(
            (variant: ExperienceProductVariantDto) => ({
              ...variant,
              variationPrices: variant.variationPrices.filter(
                (_: ExperienceVariationPricesDto, idx: number) =>
                  idx !== action.payload.index
              ),
            })
          ),
        })
      );
      return draft;
    },
    addVariationProducts: (
      draft,
      action: PayloadAction<{
        variations: VariationFe[];
      }>
    ) => {
      draft.experienceProducts.forEach((prod) => {
        prod.variants.forEach((variant) => {
          variant.variationPrices.push({
            variationId:
              action.payload.variations[action.payload.variations.length - 1]
                .id,
            id: variant.id,
            name:
              action.payload.variations[action.payload.variations.length - 1]
                .name || "",
            price: "",
            compareAtPrice: "",
          });
        });
      });

      return draft;
    },
    variationNameChange: (
      draft,
      action: PayloadAction<{
        // key: TestGroupStateValuesType;
        value: string;
        index: number;
      }>
    ) => {
      if (!draft.isSuccess) return draft;

      draft.experienceProducts = draft.experienceProducts.map((product) => {
        return {
          ...product,
          variants: product.variants.map((variant) => {
            return {
              ...variant,
              variationPrices: variant.variationPrices.map((group, index) => {
                return {
                  ...group,
                  name:
                    index === action.payload.index
                      ? action.payload.value
                      : group.name,
                };
              }),
            };
          }),
        };
      });

      return draft;
    },
  },
  selectors: {
    selectedExperienceProducts: (state) => state.experienceProducts,
    selectedExperienceProductsState: (state) =>
      state as IExperienceProductsState,
  },
});

export const {
  initExperienceProducts,
  resetExperienceProducts,
  setAllExperienceProductsLoaded,
  initProductState,
  removeExperienceProducts,
  setExperienceProducts,
  setExperienceProductsLocation,
  setIsUploadingExpProducts,
  variationNameChange,
  deleteVariationProducts,
  addVariationProducts,
  initProductsFeFromDto,
  modResetExperienceProducts,
} = experienceProducts.actions;

export const experienceProductsReducer = experienceProducts.reducer;
export const { selectedExperienceProducts, selectedExperienceProductsState } =
  experienceProducts.selectors;
